import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, Mail, ArrowLeft, LogIn } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { useAuth } from "../context/AuthContext";

const detail = (e, fb) => e?.response?.data?.detail || fb;

export const AuthDialog = () => {
  const { authOpen, closeAuth, requestCode, verify, gated } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState("email");

  // While the site is gated there is no email code: every "Sign in" goes to the password login screen.
  useEffect(() => {
    if (authOpen && gated) {
      closeAuth();
      navigate("/login");
    }
  }, [authOpen, gated, closeAuth, navigate]);
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const reset = () => {
    setStep("email");
    setCode("");
    setBusy(false);
  };

  const onOpenChange = (open) => {
    if (!open) {
      closeAuth();
      reset();
    }
  };

  const sendCode = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await requestCode(email.trim());
      setStep("code");
      toast.success(`We emailed a 6-digit code to ${email.trim()}`);
    } catch (err) {
      toast.error(detail(err, "Could not send the code. Check the email and try again."));
    } finally {
      setBusy(false);
    }
  };

  const confirmCode = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const user = await verify(email.trim(), code.trim());
      toast.success(`Signed in as ${user.email}`);
      reset();
    } catch (err) {
      toast.error(detail(err, "That code didn't work. Try again."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={authOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md rounded-3xl" data-testid="auth-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl flex items-center gap-2">
            <LogIn className="h-5 w-5 text-accent" /> Sign in to Duckify
          </DialogTitle>
          <DialogDescription>
            {step === "email"
              ? "Use your email — we'll send a one-time code. Your ducks save to your account."
              : `Enter the 6-digit code we sent to ${email.trim()}.`}
          </DialogDescription>
        </DialogHeader>

        {step === "email" ? (
          <form onSubmit={sendCode} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="auth-email">Email</Label>
              <Input
                id="auth-email"
                data-testid="auth-email-input"
                type="email"
                required
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="rounded-full h-12"
              />
            </div>
            <Button data-testid="auth-send-code-btn" type="submit" disabled={busy || !email.trim()} className="w-full rounded-full h-12 font-bold gap-2">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />} Email me a code
            </Button>
            <Link to="/login" data-testid="auth-password-link" onClick={() => onOpenChange(false)} className="block text-center text-sm text-muted-foreground hover:text-foreground">
              Have a password? Sign in with it
            </Link>
          </form>
        ) : (
          <form onSubmit={confirmCode} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="auth-code">6-digit code</Label>
              <Input
                id="auth-code"
                data-testid="auth-code-input"
                inputMode="numeric"
                pattern="[0-9]{6}"
                maxLength={6}
                required
                autoFocus
                placeholder="123456"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                className="rounded-full h-12 text-center text-2xl tracking-[0.4em] font-mono2"
              />
            </div>
            <Button data-testid="auth-verify-btn" type="submit" disabled={busy || code.length !== 6} className="w-full rounded-full h-12 font-bold gap-2">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <LogIn className="h-4 w-4" />} Sign in
            </Button>
            <button type="button" data-testid="auth-back-btn" onClick={() => setStep("email")} className="w-full text-sm text-muted-foreground hover:text-foreground inline-flex items-center justify-center gap-1.5">
              <ArrowLeft className="h-3.5 w-3.5" /> Use a different email
            </button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
